import React, { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import styles from "Styles/shell.module.css";
import formStyles from "Styles/components/form-content-wrapper.module.css";
import "Styles/index.css";
import "Styles/vars.css";
import usePageData from "Hooks/usePageData";
import { websitePrefix } from "Data/index";
import { useSession } from "Contexts/SessionContext";
import SessionLoading from "Components/SessionLoading";
import SessionExpired from "Components/SessionExpired";

import { Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import StartPage from "Pages/StartPage";
import NamePage from "Pages/NamePage";
import NameSuccessPage from "Pages/NameSuccessPage";
import CompanyNamePage from "Pages/CompanyNamePage";
import CompanySizePage from "Pages/CompanySizePage";
import MarketingChannelPage from "Pages/MarketingChannelPage";
import NurturingApproachPage from "Pages/NurturingApproachPage";
import MarketingStaffingPage from "Pages/MarketingStaffingPage";
import MarketingCapabilitiesPage from "Pages/MarketingCapabilitiesPage";
import TopGoalsPage from "Pages/TopGoalsPage";
import MarketingPainPointsPage from "Pages/MarketingPainPointsPage";
import PrivacySendPage from "Pages/PrivacySendPage";
import CompletionPage from "Pages/CompletionPage";
import NavigationWrapper from "Components/NavigationWrapper";
import BackgroundContainer from "Components/Background/BackgroundContainer";
import Logo from "Components/Logo";
import ErrorPage from "Pages/ErrorPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const { data } = usePageData();
  const location = useLocation();
  const { isLoading, error, sessionCode } = useSession();

  // Show loading screen while restoring session from URL (only during initial restore, not form submissions)
  const urlParams = new URLSearchParams(window.location.search);
  const urlSessionCode = urlParams.get("session");

  if (urlSessionCode && isLoading && !sessionCode) {
    return (
      <HelmetProvider>
        <div className={styles.mainContainer}>
          <BackgroundContainer />
          <SessionLoading />
          <span className={formStyles.formLogo}>
            <Logo large />
          </span>
        </div>
      </HelmetProvider>
    );
  }

  // Show session expired/not found when URL has session param but load failed
  if (urlSessionCode && error && !sessionCode) {
    return (
      <HelmetProvider>
        <div className={styles.mainContainer}>
          <BackgroundContainer />
          <SessionExpired sessionCode={urlSessionCode} />
          <span className={formStyles.formLogo}>
            <Logo large />
          </span>
        </div>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <div className={styles.mainContainer}>
        <ScrollToTop />
        <Helmet>
          <title>
            {websitePrefix +
              (data
                ? ` - ${data?.pageTitle}`
                : "Hububble Marketing Plan Generator")}
          </title>
          <meta name="description" content="Helmet application" />
        </Helmet>
        <BackgroundContainer />
        <Routes key={location.pathname} location={location}>
          <Route path="/" element={<StartPage />} />

          <Route path="form" element={<FormPageWrapper />}>
            <Route path="name" element={<NamePage />} />
            <Route path="name-success" element={<NameSuccessPage />} />
            <Route path="company-name" element={<CompanyNamePage />} />
            <Route path="company-size" element={<CompanySizePage />} />
            <Route
              path="marketing-channel"
              element={<MarketingChannelPage />}
            />
            <Route
              path="nurturing-approach"
              element={<NurturingApproachPage />}
            />
            <Route
              path="marketing-staffing"
              element={<MarketingStaffingPage />}
            />
            <Route
              path="marketing-capabilities"
              element={<MarketingCapabilitiesPage />}
            />
            <Route path="top-goals" element={<TopGoalsPage />} />
            <Route
              path="marketing-pain-points"
              element={<MarketingPainPointsPage />}
            />
            <Route path="privacy-send" element={<PrivacySendPage />} />
          </Route>

          {/* TODO: prevent users from going "back" from success page */}
          <Route
            path="complete"
            element={
              <RequireSession>
                <CompletionPage />
              </RequireSession>
            }
          />

          <Route path="/404" element={<ErrorPage />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        {!location.pathname.startsWith("/form") && (
          <span className={formStyles.formLogo}>
            <Logo />
          </span>
        )}
        <NavigationWrapper />
      </div>
    </HelmetProvider>
  );
}

const FormPageWrapper = () => {
  const location = useLocation();
  const { sessionCode, sessionData, isLoading } = useSession();
  const urlParams = new URLSearchParams(location.search);
  const urlSessionCode = urlParams.get("session");

  const hasSession = sessionCode || urlSessionCode;
  const isNamePage = location.pathname === "/form/name";

  // Redirect to completion page if session is already completed
  if (sessionData?.status === "completed" && sessionCode) {
    return <Navigate to={`/complete?session=${sessionCode}`} replace />;
  }

  // Redirect to the first form page if no session and not already on /form/name
  if (!isNamePage && !hasSession && !isLoading) {
    return <Navigate to="/form/name" replace />;
  }

  return (
    <div className={styles.formContainer}>
      <Outlet />
    </div>
  );
};

const RequireSession = ({ children }) => {
  const { sessionCode, isLoading } = useSession();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlSessionCode = urlParams.get("session");

  const hasSession = sessionCode || urlSessionCode;

  if (!hasSession && !isLoading) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default App;
